import { useEffect, useRef, useState } from 'react';

interface CountUpProps {
  end: number;
  formatter: (n: number) => string;
  duration?: number;
}

const CountUp = ({ end, formatter, duration = 2000 }: CountUpProps) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const startTime = performance.now();
            const tick = (now: number) => {
              const progress = Math.min((now - startTime) / duration, 1);
              const eased = 1 - Math.pow(1 - progress, 3);
              setCount(Math.floor(eased * end));
              if (progress < 1) requestAnimationFrame(tick);
              else setCount(end);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={ref}>{formatter(count)}</span>;
};

const stats = [
  {
    end: 15,
    formatter: (n: number) => `${(n / 10).toFixed(1)}억 m²`,
    label: '연간 패널 생산량',
    desc: '국내 샌드위치패널 연간 생산 면적',
    icon: 'ri-stack-fill',
  },
  {
    end: 11250,
    formatter: (n: number) =>
      n >= 10000
        ? `1억 ${(n - 10000).toLocaleString()}만 개`
        : `${n.toLocaleString()}만 개`,
    label: '연간 마감캡 수요',
    desc: '패널 1m²당 평균 75개 기준 산출',
    icon: 'ri-box-3-fill',
  },
  {
    end: 2025,
    formatter: (n: number) => `${n.toLocaleString()}억원`,
    label: '연간 시장 규모',
    desc: '마감캡 단가 180원 기준 추정치',
    icon: 'ri-bar-chart-fill',
  },
  {
    end: 180,
    formatter: (n: number) => `${n}원/개`,
    label: '단가 기준',
    desc: '마감캡 180원/개 기준 추정치',
    icon: 'ri-price-tag-3-fill',
  },
];

const MarketDataSection = () => {
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('opacity-100', 'translate-y-0');
        });
      },
      { threshold: 0.1 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="market" className="py-24 bg-[#1B2A4A] relative overflow-hidden">
      {/* 배경 글로우 */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF6B35] opacity-5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#FF6B35] opacity-5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div
          ref={titleRef}
          className="text-center mb-16 opacity-0 translate-y-6 transition-all duration-700"
        >
          <span className="text-[#FF6B35] text-sm font-semibold tracking-widest uppercase">Market Size</span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-white font-display">
            연간 <span className="text-[#FF6B35]">2,025억원</span> 규모의 시장
          </h2>
          <p className="mt-4 text-white/60 text-base max-w-xl mx-auto leading-relaxed">
            샌드위치패널 지붕 마감캡 시장은 물류·산업 시설 증가와 함께 꾸준히 성장 중입니다
          </p>
          <div className="mt-4 w-12 h-1 bg-[#FF6B35] mx-auto rounded-full" />
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="bg-white/5 border border-white/10 rounded-xl p-6 text-center hover:border-[#FF6B35]/50 hover:bg-white/10 transition-all duration-300 group"
            >
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#FF6B35]/20 mx-auto mb-4 group-hover:bg-[#FF6B35]/30 transition-colors">
                <i className={`${stat.icon} text-xl text-[#FF6B35]`} />
              </div>
              <p className="text-2xl lg:text-3xl xl:text-4xl font-bold text-white font-display mb-1 leading-tight">
                <CountUp end={stat.end} formatter={stat.formatter} duration={2200} />
              </p>
              <p className="text-[#FF6B35] text-xs font-semibold mb-2 mt-1">{stat.label}</p>
              <p className="text-white/45 text-xs leading-relaxed">{stat.desc}</p>
            </div>
          ))}
        </div>

        {/* 시장 성장 바 */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 lg:p-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <p className="text-white font-semibold text-base mb-1">시장 성장 배경</p>
              <p className="text-white/50 text-sm">산업·물류·농업 시설 신축 증가로 패널 지붕 수요 지속 확대</p>
            </div>
            <div className="flex flex-wrap gap-3">
              {[
                { label: '물류창고', pct: 88 },
                { label: '산업공장', pct: 74 },
                { label: '농업시설', pct: 62 },
                { label: '상업건물', pct: 51 },
              ].map((item) => (
                <div key={item.label} className="flex flex-col items-center gap-1 min-w-[72px]">
                  <p className="text-white/60 text-xs">{item.label}</p>
                  <div className="w-full bg-white/10 rounded-full h-1.5">
                    <div
                      className="bg-[#FF6B35] h-1.5 rounded-full transition-all duration-1000"
                      style={{ width: `${item.pct}%` }}
                    />
                  </div>
                  <p className="text-[#FF6B35] text-xs font-bold">{item.pct}%</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 출처 */}
        <p className="text-center text-white/30 text-xs mt-6">
          ※ 한국건축자재협회·통계청 기준 (2024)
        </p>
      </div>
    </section>
  );
};

export default MarketDataSection;
